import React, { useEffect, useRef, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import { CruiseInclusion } from '~/components/Cruises/pages/Inclusions/types';

import useAppNavigation from '~/hooks/useAppNavigation';

import inclusionsService from '~/services/cruises/InclusionsService';

import InclusionForm from '../components/InclusionForm';

import InclusionsFilterList from './InclusionsFilter/InclusionsFilterList';
import InclusionsItemList from './InclusionsItem/InclusionsItemList';

function InclusionsCreate() {
  const [inclusion, setInclusion] = useState<CruiseInclusion | null>(null);
  const [selectedCruiseLineId, setSelectedCruiseLineId] = useState<string | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);

  const { inclusionId } = useParams<{ inclusionId: string }>();
  const { pathname } = useLocation();
  const { replace } = useAppNavigation();
  // Routes are declared with literal `/items` and `/filters` path segments
  // (see App.tsx), so derive the tab from the URL itself rather than useParams.
  const tab: 'items' | 'filters' = pathname.includes('/filters') ? 'filters' : 'items';
  const isEvergreen = inclusion?.type === 'EVERGREEN';
  const activeTab = isEvergreen ? 'items' : tab;
  const redirectedPathRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchInclusion = async () => {
      setFetching(true);

      const inclusion = await inclusionsService.getById(inclusionId);
      setInclusion(inclusion);
      setSelectedCruiseLineId(inclusion.fkVendorId);

      setFetching(false);
    };

    fetchInclusion();
  }, [inclusionId, setInclusion, setFetching]);

  useEffect(() => {
    const redirectPath = `/cruises/inclusions/edit/${inclusionId}/items`;

    if (isEvergreen && tab === 'filters' && redirectedPathRef.current !== redirectPath) {
      redirectedPathRef.current = redirectPath;
      replace(redirectPath);
      return;
    }

    if (tab !== 'filters') {
      redirectedPathRef.current = null;
    }
  }, [inclusionId, isEvergreen, replace, tab]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Edit Inclusion</title>
      </Helmet>

      <PageHeader title="Edit Inclusion" backButton="/cruises/inclusions" />

      {fetching && <Spinner size={30} />}

      {inclusion && !fetching && (
        <Box mt={1}>
          <Box>
            <InclusionForm inclusion={inclusion} setSelectedCruiseLineId={setSelectedCruiseLineId} />
          </Box>
          <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
            <Tabs value={activeTab} variant="scrollable">
              <Tab
                value="items"
                label="Inclusion Items"
                to={`/cruises/inclusions/edit/${inclusionId}/items`}
                component={Link}
              />
              {!isEvergreen && (
                <Tab
                  value="filters"
                  label="Inclusion Filters"
                  to={`/cruises/inclusions/edit/${inclusionId}/filters`}
                  component={Link}
                />
              )}
            </Tabs>

            <Box borderTop={1} borderColor="divider">
              {activeTab === 'items' && <InclusionsItemList inclusionType={inclusion.type} />}
              {activeTab === 'filters' && !isEvergreen && <InclusionsFilterList vendorId={selectedCruiseLineId} />}
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default InclusionsCreate;
